import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/siteData';

const BG_MAIN = '#faf9f6';
const BG_CARD = '#f5f3ef';
const BG_ICON = '#eae7e1';
const TEXT_MAIN = '#1a1a1a';
const TEXT_MUTED = '#8a8580';
const TEXT_LIGHT = '#ffffff';
const PRIMARY = '#e85d4c';
const PRIMARY_DARK = '#d44a3a';
const BORDER = 'rgba(218, 213, 206, 0.7)';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileSearch('');
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen && mobileInputRef.current) {
      setTimeout(() => mobileInputRef.current?.focus(), 100);
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 200);
  };

  const filteredCategories = mobileSearch.trim()
    ? categories.filter((cat) => cat.name.toLowerCase().includes(mobileSearch.toLowerCase()))
    : categories;

  const categoryIcons: Record<string, string> = {
    'Shoes': 'ri-footprint-line',
    'Hoodies/Sweaters': 'ri-shirt-line',
    'T-Shirts': 'ri-t-shirt-line',
    'Jackets': 'ri-cloud-line',
    'Pants/Shorts': 'ri-pantone-line',
    'Headwear': 'ri-user-smile-line',
    'Sets': 'ri-stack-line',
    'Underwear/Underpants': 'ri-checkbox-blank-circle-line',
    'Jersey': 'ri-vip-crown-line',
    'Accessories': 'ri-heart-line',
    'Bags': 'ri-shopping-bag-line',
    'Belts': 'ri-link',
    'Perfumes': 'ri-drop-line',
    'Other Stuff': 'ri-more-line',
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 h-[72px] flex items-center bg-background-50/90 backdrop-blur-sm transition-all duration-300">
        <div className="w-full px-6 md:px-10 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 tracking-tight">
              Superbuy <span className="text-primary-500">Sheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
              Home
            </Link>
            <Link to="/article/superbuy-sheet-beginner-guide" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
              Guides
            </Link>

            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[720px] bg-background-50 border border-background-200/70 rounded-2xl p-5 grid grid-cols-2 gap-1" style={{ animation: 'fadeInDropdown 0.15s ease-out forwards' }}>
                  <style>{`@keyframes fadeInDropdown { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center justify-between px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-accent-100 hover:text-foreground-950 transition-colors group"
                    >
                      <span className="flex items-center gap-2.5">
                        <span className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center group-hover:bg-accent-200/50 transition-colors">
                          <i className={`${categoryIcons[cat.name] || 'ri-folder-line'} text-foreground-400 group-hover:text-foreground-600 text-xs`}></i>
                        </span>
                        {cat.name}
                      </span>
                      <span className="text-xs text-foreground-400 group-hover:text-foreground-600 transition-colors">{cat.count}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <i className={`text-xl transition-transform duration-200 ${mobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-foreground-950/60 backdrop-blur-sm transition-opacity duration-300 md:hidden ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        style={{ zIndex: 9998 }}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu Panel */}
      <div
        className="fixed inset-y-0 w-full max-w-[400px] md:hidden"
        style={{
          zIndex: 9999,
          backgroundColor: BG_MAIN,
          right: mobileMenuOpen ? '0' : '-100%',
          transition: 'right 300ms ease-out',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        }}
      >
        {/* Header */}
        <div
          className="absolute top-0 left-0 right-0 flex items-center justify-between px-6"
          style={{ height: '72px', backgroundColor: BG_MAIN, borderBottom: `1px solid ${BORDER}` }}
        >
          <span className="font-heading text-lg font-semibold" style={{ color: TEXT_MAIN }}>
            Superbuy <span style={{ color: PRIMARY }}>Sheet</span>
          </span>
          <button
            className="w-10 h-10 flex items-center justify-center transition-colors"
            style={{ color: TEXT_MUTED }}
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Quick Nav Links */}
        <div
          className="absolute left-0 right-0 flex items-center gap-2 px-6"
          style={{ top: '72px', height: '58px', backgroundColor: BG_MAIN, borderBottom: `1px solid ${BORDER}` }}
        >
          <Link
            to="/"
            onClick={() => setMobileMenuOpen(false)}
            className="flex-1 text-center py-2.5 rounded-xl text-sm font-medium transition-colors"
            style={{ backgroundColor: BG_CARD, color: TEXT_MAIN }}
          >
            Home
          </Link>
          <Link
            to="/article/superbuy-sheet-beginner-guide"
            onClick={() => setMobileMenuOpen(false)}
            className="flex-1 text-center py-2.5 rounded-xl text-sm font-medium transition-colors"
            style={{ backgroundColor: BG_CARD, color: TEXT_MAIN }}
          >
            Guides
          </Link>
        </div>

        {/* Search */}
        <div
          className="absolute left-0 right-0 px-6"
          style={{ top: '130px', height: '74px', backgroundColor: BG_MAIN, borderBottom: `1px solid ${BORDER}` }}
        >
          <div className="relative h-full flex items-center">
            <i className="ri-search-line absolute left-3.5 text-sm" style={{ color: TEXT_MUTED }}></i>
            <input
              ref={mobileInputRef}
              type="text"
              placeholder="Search categories..."
              value={mobileSearch}
              onChange={(e) => setMobileSearch(e.target.value)}
              className="w-full pl-10 pr-10 py-3 rounded-xl text-sm outline-none border border-transparent transition-colors"
              style={{ backgroundColor: BG_CARD, color: TEXT_MAIN }}
            />
            {mobileSearch && (
              <button
                className="absolute right-3 w-6 h-6 rounded-full flex items-center justify-center transition-colors"
                style={{ backgroundColor: BG_ICON, color: TEXT_MUTED }}
                onClick={() => setMobileSearch('')}
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            )}
          </div>
        </div>

        {/* Categories Grid */}
        <div
          className="absolute left-0 right-0 px-5 overflow-y-auto"
          style={{ top: '204px', bottom: '120px', backgroundColor: BG_MAIN }}
        >
          <p className="text-xs font-semibold uppercase tracking-wider mb-3 px-1" style={{ color: TEXT_MUTED }}>
            {mobileSearch.trim() ? `${filteredCategories.length} Categories Found` : 'All 14 Categories'}
          </p>

          {filteredCategories.length === 0 ? (
            <div className="text-center py-10">
              <div
                className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-4"
                style={{ backgroundColor: BG_CARD }}
              >
                <i className="ri-search-line text-xl" style={{ color: '#c5c0b8' }}></i>
              </div>
              <p className="text-sm" style={{ color: TEXT_MUTED }}>No categories match &ldquo;{mobileSearch}&rdquo;</p>
              <button
                className="mt-3 text-xs font-medium transition-colors"
                style={{ color: PRIMARY }}
                onClick={() => setMobileSearch('')}
              >
                Clear search
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', paddingBottom: '16px' }}>
              {filteredCategories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="group flex flex-col items-center gap-2 px-2 py-4 rounded-xl transition-all duration-200 cursor-pointer"
                  style={{
                    backgroundColor: cat.hot ? PRIMARY : BG_CARD,
                    color: cat.hot ? TEXT_LIGHT : TEXT_MAIN,
                  }}
                >
                  <span
                    className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
                    style={{
                      backgroundColor: cat.hot ? 'rgba(255,255,255,0.2)' : BG_ICON,
                    }}
                  >
                    <i className={`${categoryIcons[cat.name] || 'ri-folder-line'} text-base`} style={{ color: cat.hot ? TEXT_LIGHT : TEXT_MUTED }}></i>
                  </span>
                  <span className="text-xs font-semibold text-center leading-tight">
                    {cat.name}
                  </span>
                  <span className="text-[10px] transition-colors"
                    style={{ color: cat.hot ? 'rgba(255,255,255,0.7)' : TEXT_MUTED }}
                  >
                    {cat.count}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* CTA */}
        <div
          className="absolute bottom-0 left-0 right-0 px-6"
          style={{ height: '120px', backgroundColor: BG_MAIN, borderTop: `1px solid ${BORDER}` }}
        >
          <div className="flex flex-col items-center justify-center h-full">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="block w-full text-center py-3.5 rounded-full text-sm font-semibold transition-colors"
              style={{ backgroundColor: PRIMARY, color: TEXT_LIGHT }}
            >
              Buy It Now
            </a>
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="block w-full text-center py-2.5 mt-1 text-sm transition-colors"
              style={{ color: TEXT_MUTED }}
            >
              Back to Homepage
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}